/**
 * The foot of the next level's card: what upgrading to it costs and how long it takes, and the
 * Upgrade button, saying why not when it can't; while it upgrades, how far along it is.
 */
import type { JSX } from 'react';

import { costEntries, spendableOf } from '@game/planet/pockets';
import { startUpgrade, type Upgrade } from '@game/upgrade/upgrades';
import { duration, reasonText, t } from '@i18n/index';
import { haptic } from '@platform/haptics';
import { CostChips } from '@ui/components/CostChips';
import { useGame } from '@ui/hooks';
import { Icon } from '@ui/icons';

import './next.css';

export function NextLevel({
  upgrade,
  tile,
  name,
}: {
  upgrade: Upgrade;
  tile: number;
  name: string;
}): JSX.Element {
  const game = useGame();
  if (upgrade.kind === 'underway') {
    const percent = Math.round(upgrade.progress * 100);
    return (
      <div className="lfoot underway">
        <div
          className="upgrade-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-label={t('upgrade.underway', { n: upgrade.to })}
        >
          {/* a sliver from the start: it has begun */}
          <i className="fill" style={{ width: `${String(Math.max(3, percent))}%` }} />
        </div>
        <small className="time">
          <Icon id="clock" />
          {t('upgrade.left', { time: duration(upgrade.left) })}
        </small>
        <p className="note">{t('upgrade.working')}</p>
      </div>
    );
  }
  const { check } = upgrade;
  const reason =
    check.state === 'refused'
      ? reasonText(check.refusal)
      : check.state === 'waiting'
        ? t('reason.waiting')
        : null;
  const s = game.store.get();
  const aria = t('upgrade.button-aria', { name: name.toLowerCase(), n: upgrade.to });
  return (
    <div className="lfoot">
      <div className="lprice">
        {costEntries(upgrade.cost).length > 0 ? (
          <CostChips cost={upgrade.cost} have={spendableOf(s.view, s.me?.id ?? null)} />
        ) : (
          <span className="free">{t('build.free')}</span>
        )}
        {upgrade.ms > 0 && (
          <small className="time">
            <Icon id="clock" />
            {duration(upgrade.ms)}
          </small>
        )}
      </div>
      <button
        type="button"
        id="upgrade"
        className="upgrade-btn"
        aria-disabled={check.state !== 'ok'}
        aria-label={reason ? `${aria}: ${reason}` : aria}
        onClick={() => haptic(startUpgrade(game, tile) ? 'build' : 'refused')}
      >
        <Icon id="up" />
        {t('upgrade.button')}
      </button>
      {reason && <p className="note warn">{reason}</p>}
    </div>
  );
}
